import * as React from 'react';
import type { ChatStore, Id, InboundEvent, Message } from '@norkin/chat-core';
import blastApi from '@features/flows/api/blast-api';
import { useRuntimeStore } from '@features/flows/runtime/useRuntimeStore';

type Params = {
  store: ChatStore;
  threadId: Id;
  flowId: string;
  workspaceId?: string | null;
};

type RunnerState = {
  runId?: string;
  isRunning: boolean;
  send(assistantMessageId: Id): Promise<void>;
  reset(): void;
};

function storageKey(workspaceId: string | null | undefined, flowId: string) {
  return `flowchat.runId.${workspaceId || 'default'}.${flowId}`;
}

function readStoredRunId(workspaceId: string | null | undefined, flowId: string): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const value = window.localStorage.getItem(storageKey(workspaceId, flowId));
    return value || undefined;
  } catch {
    return undefined;
  }
}

function writeStoredRunId(workspaceId: string | null | undefined, flowId: string, runId?: string) {
  if (typeof window === 'undefined') return;
  const key = storageKey(workspaceId, flowId);
  try {
    if (runId) window.localStorage.setItem(key, runId);
    else window.localStorage.removeItem(key);
  } catch {
    // ignore storage errors
  }
}

type ConversationMessagePayload = { role: 'user' | 'assistant'; content: string };

function extractTextFromMessage(message: Message | undefined): string {
  if (!message) return '';
  try {
    return message.content
      .map((part) => {
        if (!part) return '';
        if ((part.type === 'text' || part.type === 'reasoning') && typeof part.text === 'string') {
          return part.text;
        }
        if (part.type === 'tool-result' && typeof (part as any).result === 'string') {
          return (part as any).result;
        }
        return '';
      })
      .join('')
      .trim();
  } catch {
    return '';
  }
}

function collectConversationHistory(store: ChatStore, threadId: Id, excludeMessageId?: Id): ConversationMessagePayload[] {
  try {
    const messages = store.getState().getThreadMessages(threadId);
    const history: ConversationMessagePayload[] = [];
    for (const message of messages) {
      if (!message) continue;
      if (excludeMessageId && message.id === excludeMessageId) continue;
      if (message.role !== 'user' && message.role !== 'assistant') continue;
      if (message.role === 'assistant' && message.status !== 'complete') continue;
      const text = extractTextFromMessage(message);
      if (!text) continue;
      history.push({ role: message.role, content: text });
    }
    return history;
  } catch {
    return [];
  }
}

function ingestEvents(store: ChatStore, events: InboundEvent[]) {
  try {
    store.getState().ingestBatch(events);
  } catch (error) {
    console.error('[flow-chat] Failed to ingest chat events', error);
  }
}

function finalizeAssistantMessage(store: ChatStore, threadId: Id, assistantMessageId: Id, text: string) {
  const events: InboundEvent[] = [];
  if (text) {
    events.push({ type: 'message:delta', threadId, messageId: assistantMessageId, textDelta: text });
  }
  events.push({ type: 'message:completed', messageId: assistantMessageId });
  ingestEvents(store, events);
}

function resolveResultText(payload: any): { text: string; error?: string } {
  if (!payload || typeof payload !== 'object') {
    return { text: '' };
  }
  const error = payload.error || payload.err;
  if (error) {
    const msg = typeof error?.message === 'string' && error.message.trim() ? error.message.trim() : 'Run failed';
    return { text: `⚠️ ${msg}`, error: msg };
  }

  const result = payload.result || payload;
  if (result) {
    if (typeof result.text === 'string' && result.text.trim()) {
      return { text: result.text.trim() };
    }
    const fromMessages = collectAssistantMessages(result.messages ?? result.chat?.messages);
    if (fromMessages) {
      return { text: fromMessages };
    }
  }

  const fallbackMessages = collectAssistantMessages(payload.messages);
  if (fallbackMessages) {
    return { text: fallbackMessages };
  }

  return { text: '' };
}

function collectAssistantMessages(raw: any): string {
  if (!Array.isArray(raw)) return '';
  return raw
    .filter((entry) => entry && (entry.role === 'assistant' || entry.role === 'model'))
    .map((entry) => {
      if (typeof entry.content === 'string') return entry.content;
      if (Array.isArray(entry.content)) {
        return entry.content
          .map((part) => {
            if (!part) return '';
            if (typeof part === 'string') return part;
            if (typeof part.text === 'string') return part.text;
            if (typeof part.value === 'string') return part.value;
            return '';
          })
          .join('');
      }
      if (typeof entry.text === 'string') return entry.text;
      return '';
    })
    .filter(Boolean)
    .join('\n\n')
    .trim();
}

function extractTextCandidate(candidate: any): string {
  if (!candidate) return '';
  if (typeof candidate === 'string') {
    const trimmed = candidate.trim();
    return trimmed;
  }
  if (typeof candidate.text === 'string' && candidate.text.trim()) {
    return candidate.text.trim();
  }
  if (Array.isArray(candidate.messages)) {
    const text = collectAssistantMessages(candidate.messages);
    if (text) return text;
  }
  if (Array.isArray(candidate)) {
    const text = collectAssistantMessages(candidate);
    if (text) return text;
  }
  return '';
}

function resolveChatReturnMessage(event: any): string {
  const inputSources = [event?.scope?.inputs, event?.payload?.inputs, event?.inputs];
  for (const source of inputSources) {
    const text = extractTextCandidate(source);
    if (text) return text;
    if (source && typeof source === 'object') {
      const nested = extractTextCandidate((source as any).text ?? (source as any).value);
      if (nested) return nested;
    }
  }

  const outputSources = [event?.scope?.outputs, event?.payload?.outputs, event?.outputs];
  for (const source of outputSources) {
    const text = extractTextCandidate(source);
    if (text) return text;
    if (source && typeof source === 'object') {
      const result = (source as any).result;
      const fromResult = extractTextCandidate(result);
      if (fromResult) return fromResult;
      const messages = (source as any).messages ?? result?.messages;
      const fromMessages = collectAssistantMessages(messages);
      if (fromMessages) return fromMessages;
    }
  }

  const fallback = collectAssistantMessages(event?.payload?.result?.messages);
  if (fallback) return fallback;
  return '';
}

function resolveErrorMessage(error: unknown): string {
  if (!error) return 'Failed to send message.';
  if (typeof (error as any)?.friendlyMessage === 'string') {
    return (error as any).friendlyMessage as string;
  }
  if (error instanceof Error) return error.message || 'Failed to send message.';
  if (typeof error === 'string') return error;
  return 'Failed to send message.';
}

export function useFlowChatRunner({ store, threadId, flowId, workspaceId }: Params): RunnerState {
  const [runId, setRunId] = React.useState<string | undefined>(undefined);
  const [isRunning, setIsRunning] = React.useState(false);
  const esRef = React.useRef<EventSource | null>(null);
  const runIdRef = React.useRef<string | undefined>(undefined);
  const assistantRef = React.useRef<Id | null>(null);
  const assistantCompletedRef = React.useRef(false);
  const reconnectTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearReconnectTimer = React.useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    runIdRef.current = runId;
  }, [runId]);

  React.useEffect(() => {
    return () => {
      if (esRef.current) {
        try {
          esRef.current.close();
        } catch {
          // ignore
        }
        esRef.current = null;
      }
      clearReconnectTimer();
    };
  }, [clearReconnectTimer]);

  React.useEffect(() => {
    blastApi.setWorkspace(workspaceId ?? null);
  }, [workspaceId]);

  React.useEffect(() => {
    if (esRef.current) {
      try {
        esRef.current.close();
      } catch {
        // ignore
      }
      esRef.current = null;
    }
    clearReconnectTimer();
    setRunId(undefined);
    runIdRef.current = undefined;

    const stored = readStoredRunId(workspaceId ?? null, flowId);
    if (!stored) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const statusResp = await blastApi.getRunStatus(stored);
        const rawStatus =
          (typeof statusResp?.status === 'string' && statusResp.status) ||
          (typeof (statusResp as any)?.state === 'string' && (statusResp as any).state) ||
          undefined;
        const normalized = rawStatus ? rawStatus.toLowerCase() : undefined;
        const isTerminal = normalized
          ? ['completed', 'complete', 'failed', 'fail', 'canceled', 'cancelled', 'error', 'stopped'].includes(normalized)
          : false;

        if (!cancelled && !isTerminal) {
          setRunId(stored);
          runIdRef.current = stored;
          try {
            const runtime = useRuntimeStore.getState();
            runtime.resetRun(stored, flowId);
            if (normalized === 'running') {
              runtime.setRunStatus('running');
            }
          } catch {
            // ignore runtime rehydration errors
          }
        } else if (!cancelled) {
          writeStoredRunId(workspaceId ?? null, flowId, undefined);
        }
      } catch {
        if (!cancelled) {
          writeStoredRunId(workspaceId ?? null, flowId, undefined);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [workspaceId, flowId, clearReconnectTimer]);

  const subscribeToRun = React.useCallback((id: string, assistantMessageId: Id) => {
    if (!id) return;
    if (esRef.current) {
      try { esRef.current.close(); } catch { /* noop */ }
    }
    clearReconnectTimer();
    const es = blastApi.createEventSource(id, { replay: 'missed' });
    esRef.current = es;
    assistantRef.current = assistantMessageId;

    es.addEventListener('open', () => {
      clearReconnectTimer();
    });

    es.addEventListener('telemetry', (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        useRuntimeStore.getState().applyTelemetry(data);

        if (
          !assistantCompletedRef.current &&
          typeof data?.nodeId === 'string' &&
          data.nodeId.startsWith('chat.return') &&
          data?.action === 'complete'
        ) {
          const assistantId = assistantRef.current;
          const text = resolveChatReturnMessage(data);
          if (assistantId && text) {
            finalizeAssistantMessage(store, threadId, assistantId, text);
            assistantCompletedRef.current = true;
            setIsRunning(false);
          }
        }
      } catch (error) {
        console.warn('[flow-chat] Failed to parse telemetry event', error);
      }
    });

    es.addEventListener('end', (event: MessageEvent) => {
      let parsed: any;
      try {
        parsed = JSON.parse(event.data);
      } catch {
        parsed = undefined;
      }
      const { text, error } = resolveResultText(parsed);
      const assistantId = assistantRef.current;
      if (assistantId && !assistantCompletedRef.current) {
        const output = text || '✓ Flow completed.';
        finalizeAssistantMessage(store, threadId, assistantId, output);
        assistantCompletedRef.current = true;
      }
      const normalizedStatus = typeof parsed?.status === 'string' ? parsed.status.toLowerCase() : undefined;
      try {
        const runtime = useRuntimeStore.getState();
        if (normalizedStatus === 'canceled' || normalizedStatus === 'cancelled' || normalizedStatus === 'aborted' || normalizedStatus === 'stopped') {
          runtime.setRunStatus('canceled');
        } else if (normalizedStatus === 'failed' || normalizedStatus === 'error' || error) {
          runtime.setRunStatus('failed');
        } else {
          runtime.setRunStatus('succeeded');
        }
      } catch {
        // ignore
      }
      clearReconnectTimer();
      setIsRunning(false);
      setRunId(undefined);
      runIdRef.current = undefined;
      writeStoredRunId(workspaceId ?? null, flowId, undefined);
      try { es.close(); } catch { /* ignore */ }
      if (esRef.current === es) esRef.current = null;
    });

    es.onerror = () => {
      if (reconnectTimerRef.current !== null) return;
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        const assistantId = assistantRef.current;
        if (assistantId) {
          finalizeAssistantMessage(
            store,
            threadId,
            assistantId,
            '⚠️ Connection lost. The run may still be completing server-side.'
          );
          assistantCompletedRef.current = true;
        }
        setIsRunning(false);
        setRunId(undefined);
        runIdRef.current = undefined;
        writeStoredRunId(workspaceId ?? null, flowId, undefined);
        try { es.close(); } catch { /* ignore */ }
        if (esRef.current === es) esRef.current = null;
      }, 45000);
    };
  }, [store, threadId, clearReconnectTimer, flowId, workspaceId]);

  const send = React.useCallback(async (assistantMessageId: Id) => {
    const conversation = collectConversationHistory(store, threadId, assistantMessageId);
    const lastEntry = conversation.length ? conversation[conversation.length - 1] : undefined;
    if (!lastEntry || lastEntry.role !== 'user') return;
    setIsRunning(true);
    assistantRef.current = assistantMessageId;
    assistantCompletedRef.current = false;

    try {
      const response = await blastApi.startRun({
        flowId,
        inputs: {
          messages: conversation,
        },
      });
      const id = response.runId;
      setRunId(id);
      runIdRef.current = id;
      writeStoredRunId(workspaceId ?? null, flowId, id);
      try {
        const runtime = useRuntimeStore.getState();
        runtime.resetRun(id, flowId);
        runtime.setRunStatus('running');
      } catch {
        // ignore
      }
      subscribeToRun(id, assistantMessageId);
    } catch (error) {
      const message = resolveErrorMessage(error);
      finalizeAssistantMessage(store, threadId, assistantMessageId, `⚠️ ${message}`);
      assistantCompletedRef.current = true;
      setIsRunning(false);
    }
  }, [flowId, store, subscribeToRun, threadId, workspaceId]);

  const reset = React.useCallback(() => {
    if (esRef.current) {
      try { esRef.current.close(); } catch { /* ignore */ }
      esRef.current = null;
    }
    clearReconnectTimer();
    assistantRef.current = null;
    assistantCompletedRef.current = false;
    setIsRunning(false);
    setRunId(undefined);
    runIdRef.current = undefined;
    writeStoredRunId(workspaceId ?? null, flowId, undefined);
    try { useRuntimeStore.getState().resetRun(undefined, undefined); } catch {
      // ignore
    }
  }, [clearReconnectTimer, flowId, workspaceId]);

  return React.useMemo<RunnerState>(() => ({ runId, isRunning, send, reset }), [runId, isRunning, send, reset]);
}
